/* eslint-disable no-underscore-dangle */
import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { FieldControl, FieldGroup, FormBuilder, Validators } from 'react-reactive-form';
import { Link } from 'react-router-dom';
import { AppState } from '../../../../store/store.types';
import { CarModelActionTypes } from '../../types/carmodel.types';
import {
  addNewCarModel,
  deleteCarModel,
  editCarModel,
  getCarModel,
  getCarModelsList
} from '../../actions/carmodel.actions';
import { NumberInput, TextInput } from '../../../../components/forms/Input';
import StatusLabel from '../../../../components/forms/StatusLabel';
import Checkbox from '../../../../components/forms/Checkbox';
import placeHolder from '../../../../assets/img/nocar.svg';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import infoIcon from '../../../../assets/img/info-circle.svg';

import {
  AcrissCodeRelatedFields,
  AirCoVal,
  AlternativeCarModel,
  CarModelDriveType,
  carModelDriveTypeOptions,
  carModelEditRoles,
  CarModelFeatureModel,
  CarModelFuelType,
  carModelFuelTypeOptions,
  CarModelListFilters,
  CarModelModel,
  CarModelRequest,
  CarModelTransmissionType,
  carModelTransmissionTypeOptions,
  FuelAcMap,
  TransmissionDriveMap
} from '../../../../api/core/core.types';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import Select from '../../../../components/forms/Select';
import { SelectedOption, StatusType } from '../../../../api/api.types';
import { AdminApi } from '../../../../api/api.apiclient';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import ImageUpload from '../../../../components/forms/ImageUpload';
import SectionTitle from '../../../../components/forms/SectionTitle';
import { MultiSelect } from '../../../../components/forms/MultiSelect';
import { TwoColumnsSelectValueModel } from '../../../../components/forms/TwoColumnsSelect';
import { WithTranslation, withTranslation } from 'react-i18next';
import {
  adminModuleEditRoles,
  ClassifierModel,
  ClassifierTypes,
  SupplierModel
} from '../../../../api/admin/admin.types';
import { formatDateTime } from '../../../../helpers/date.helper';

const transmissionDriveMap: TransmissionDriveMap = {
  'M': { transmission: CarModelTransmissionType.MANUAL, drive: CarModelDriveType.UNSPECIFIED },
  'N': { transmission: CarModelTransmissionType.MANUAL, drive: CarModelDriveType._4WD },
  'C': { transmission: CarModelTransmissionType.MANUAL, drive: CarModelDriveType.AWD },
  'A': { transmission: CarModelTransmissionType.AUTOMATIC, drive: CarModelDriveType.UNSPECIFIED },
  'B': { transmission: CarModelTransmissionType.AUTOMATIC, drive: CarModelDriveType._4WD },
  'D': { transmission: CarModelTransmissionType.AUTOMATIC, drive: CarModelDriveType.AWD }
};

const fuelAcMap: FuelAcMap = {
  'R': { fuel: CarModelFuelType.UNSPECIFIED, airCo: AirCoVal.NO_AC },
  'N': { fuel: CarModelFuelType.UNSPECIFIED, airCo: AirCoVal.WITH_AC },
  'D': { fuel: CarModelFuelType.DIESEL, airCo: AirCoVal.NO_AC },
  'Q': { fuel: CarModelFuelType.DIESEL, airCo: AirCoVal.WITH_AC },
  'H': { fuel: CarModelFuelType.HYBRID, airCo: AirCoVal.NO_AC },
  'I': { fuel: CarModelFuelType.HYBRID, airCo: AirCoVal.WITH_AC },
  'E': { fuel: CarModelFuelType.ELECTRIC, airCo: AirCoVal.NO_AC },
  'C': { fuel: CarModelFuelType.ELECTRIC, airCo: AirCoVal.WITH_AC },
  'L': { fuel: CarModelFuelType.LPG_COMPRESSED_GAS, airCo: AirCoVal.NO_AC },
  'S': { fuel: CarModelFuelType.LPG_COMPRESSED_GAS, airCo: AirCoVal.WITH_AC },
  'A': { fuel: CarModelFuelType.HYDROGEN, airCo: AirCoVal.NO_AC },
  'B': { fuel: CarModelFuelType.HYDROGEN, airCo: AirCoVal.WITH_AC }
};

interface CarModelLocationProps {
  id: string;
}

interface CarModelDetailProps extends RouteComponentProps<CarModelLocationProps>, WithTranslation {
  selectedCarModel: CarModelModel | null;
  getSelectedCarModel: (id: number) => void;
  addNewCarModel: (request: CarModelRequest) => void;
  editCarModel: (id: number, request: CarModelRequest) => void;
  deleteCarModel: (id: number) => void;
  getCarModels: () => Promise<void>;
  carModels: CarModelModel[];
  isNew?: boolean;
  supplier: SupplierModel | null;
  postSuccess: boolean | null;
  lastFetch: number | null;
  user: UserModel | null;
  filters: CarModelListFilters | null;
}

interface CarModelDetailState {
  isModalOpen: boolean;
  carModelFeatures: ClassifierModel[];
  selectedFeatures: string[];
  selectedAlternativeCars: number[];
  isEdit: boolean;
  canEditFeatures: boolean;
  canEditAlternativeCars: boolean;
  editRank?: number;
  acrissCodeInvalid: {
    transInvalid: boolean;
    driveInvalid: boolean;
    fuelInvalid: boolean;
    acInvalid: boolean
  };
}

class CarModelDetails extends React.Component<CarModelDetailProps, CarModelDetailState> {

  readonly state = {
    isModalOpen: false,
    carModelFeatures: [] as ClassifierModel[],
    selectedFeatures: [],
    selectedAlternativeCars: [],
    isEdit: false,
    canEditFeatures: false,
    canEditAlternativeCars: false,
    editRank: undefined,
    acrissCodeInvalid: {
      transInvalid: false,
      driveInvalid: false,
      fuelInvalid: false,
      acInvalid: false
    }
  };

  form = FormBuilder.group({
    passengerCount: [ null, [ Validators.required, Validators.min(0) ] ],
    doorCount: [ null, [ Validators.required, Validators.min(0) ] ],
    baggageCountBig: [ null, [ Validators.required, Validators.min(0) ] ],
    baggageCountSmall: [ null, [ Validators.required, Validators.min(0) ] ],
    youngDriverAge: [ null, [ Validators.min(0) ] ],
    minDriverAge: [ null, [ Validators.required, Validators.min(0) ] ],
    name: [ '', Validators.required ],
    tag: [ '' ],
    image: [ null ],
    supplierReferenceCode: [ '' ],
    acrissCode: [ '', Validators.required ],
    displayName: [ '' ],
    transmissionType: [ '', Validators.required ],
    driveType: [ '', Validators.required ],
    fuelType: [ '', Validators.required ],
    isGuaranteedModel: [ true, Validators.required ],
    hasPrice: [ true, Validators.required ],
    hasAirConditioning: [ true, Validators.required ],
    isOneWayRentalAllowed: [ true, Validators.required ],
    active: [ true ],
    alternatives: [ { items: [], selected: [] } ],
    features: [ { items: [], selected: [] } ]
  });

  async componentDidMount() {
    const { id } = this.props.match.params;
    if (id) {
      await this.props.getSelectedCarModel(Number(id));
    }

    if (this.props.isNew) {
      this.setState({ canEditFeatures: true });
    }

    if (!this.props.carModels.length || this.props.filters?.tag?.length) {
      await this.props.getCarModels();
      if (this.props.carModels.length) this.assignAlternativeCarsToForm(this.props.carModels);
    } else {
      this.assignAlternativeCarsToForm(this.props.carModels);
    }

    await AdminApi.Classifiers.listAllClassifiersByType(ClassifierTypes.CAR_MODEL_FEATURE).then((response) => {
      this.setState({ carModelFeatures: response });
      this.assignFeaturesToForm(response);
    });

    this.fillAcrissCodeRelatedFields();
    const acrissCodeFields: any = {
      transmissionType: this.form.controls.transmissionType.value,
      fuelType: this.form.controls.fuelType.value,
      driveType: this.form.controls.driveType.value,
      hasAirCo: this.form.controls.hasAirConditioning.value
    };
    this.checkIfAcrissCodeValid({ ...(acrissCodeFields as AcrissCodeRelatedFields) });
  }

  componentDidUpdate(prevProps: CarModelDetailProps) {
    if ((this.props.isNew) && this.props.postSuccess && this.props.selectedCarModel) {
      this.props.history.push(`/setup/carmodels/${this.props.selectedCarModel.id}/view`);
    }
    if (this.props.selectedCarModel && (!prevProps.selectedCarModel || this.props.selectedCarModel !== prevProps.selectedCarModel)) {
      this.updateFormValue();

      const acrissCodeFields: any = {
        transmissionType: this.form.controls.transmissionType.value,
        fuelType: this.form.controls.fuelType.value,
        driveType: this.form.controls.driveType.value,
        hasAirCo: this.form.controls.hasAirConditioning.value
      };
      this.checkIfAcrissCodeValid({ ...(acrissCodeFields as AcrissCodeRelatedFields) });
    }
  }

  fillAcrissCodeRelatedFields() {
    this.form.controls.hasAirConditioning.valueChanges.subscribe((val: boolean) => {
      if (this.form.controls.acrissCode.value && this.form.controls.acrissCode.value.length >= 4) {
        const acChar: keyof FuelAcMap = this.form.controls.acrissCode.value.charAt(3);
        const acVal = fuelAcMap[ acChar ];
        const currentVal = val ? AirCoVal.WITH_AC : AirCoVal.NO_AC;
        if (acVal) {
          const ac = acVal.airCo;
          if (ac !== currentVal) {
            this.setState({ acrissCodeInvalid: { ...this.state.acrissCodeInvalid, acInvalid: true } });
          } else {
            this.setState({ acrissCodeInvalid: { ...this.state.acrissCodeInvalid, acInvalid: false } });
          }
        } else {
          this.setState({ acrissCodeInvalid: { ...this.state.acrissCodeInvalid, acInvalid: false } });
        }
      } else {
        this.setState({ acrissCodeInvalid: { ...this.state.acrissCodeInvalid, acInvalid: false } });
      }
    });
    this.form.controls.acrissCode.valueChanges.subscribe((val: string) => {
      if (val.length >= 3) {
        const transmissionDriveKey = val.charAt(2) as keyof TransmissionDriveMap;
        const transDriveVal = transmissionDriveMap[ transmissionDriveKey ];
        if (transDriveVal) {
          const trans = transDriveVal.transmission;
          const drive = transDriveVal.drive;
          this.form.patchValue({ transmissionType: trans, driveType: drive });
        }
      }
      if (val.length >= 4) {
        const fuelAcKey = val.charAt(3) as keyof FuelAcMap;
        const fuelAcVal = fuelAcMap[ fuelAcKey ];
        if (fuelAcVal) {
          const fuel = fuelAcVal.fuel;
          const ac = fuelAcVal.airCo;
          this.form.patchValue({ fuelType: fuel, hasAirConditioning: ac === AirCoVal.WITH_AC });
        }
      }
      this.setState({
        acrissCodeInvalid: {
          ...{
            transInvalid: false,
            driveInvalid: false,
            fuelInvalid: false,
            acInvalid: false
          }
        }
      });
    });
  }

  assignFeaturesToForm = (items: ClassifierModel[]) => {
    const form = this.form.controls.features.value as TwoColumnsSelectValueModel;
    items.forEach((item, idx) => {
      const indexedObj = { ...item, id: idx };
      form.items.push(indexedObj);
    });

    if (this.props.selectedCarModel) {
      const codes = this.props.selectedCarModel.carModelFeatures.map((itm: CarModelFeatureModel) => itm.featureCode);
      form.selected = codes;
      this.setState({
        selectedFeatures: codes
      });
    }
  };

  onDeleteConfirm = () => {
    const { id } = this.props.match.params;
    this.props.deleteCarModel(Number(id));
    this.props.history.push('/setup/carmodels/');
  };

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();
    if (this.form.invalid) {
      return;
    }

    if (!this.props.isNew) {
      let base64Image;
      if (form.image.value && !form.image.value.includes('https://')) base64Image = form.image.value;
      const request: CarModelRequest = {
        ...this.props.selectedCarModel,
        acrissCode: form.acrissCode.value,
        passengerCount: form.passengerCount.value,
        doorCount: form.doorCount.value,
        baggageCountBig: form.baggageCountBig.value,
        baggageCountSmall: form.baggageCountSmall.value,
        youngDriverAge: form.youngDriverAge.value,
        minDriverAge: form.minDriverAge.value,
        name: form.name.value,
        tag: form.tag.value,
        base64Image,
        supplierReferenceCode: form.supplierReferenceCode.value,
        displayName: form.displayName.value,
        transmissionType: form.transmissionType.value,
        driveType: form.driveType.value,
        fuelType: form.fuelType.value,
        isGuaranteedModel: form.isGuaranteedModel.value,
        hasPrice: form.hasPrice.value,
        isOneWayRentalAllowed: form.isOneWayRentalAllowed.value,
        hasAirConditioning: form.hasAirConditioning.value,
        statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE,
        alternativeCars: this.convertAlternatives(),
        carModelFeatures: this.convertFeatures()
      };
      const { id } = this.props.match.params;
      this.props.editCarModel(Number(id), request);
      this.setState({ isEdit: false, canEditFeatures: false, canEditAlternativeCars: false });


    } else {
      const request: CarModelRequest = {
        acrissCode: form.acrissCode.value,
        passengerCount: form.passengerCount.value,
        doorCount: form.doorCount.value,
        baggageCountBig: form.baggageCountBig.value,
        baggageCountSmall: form.baggageCountSmall.value,
        youngDriverAge: form.youngDriverAge.value,
        minDriverAge: form.minDriverAge.value,
        name: form.name.value,
        tag: form.tag.value,
        base64Image: form.image.value,
        supplierReferenceCode: form.supplierReferenceCode.value,
        displayName: form.displayName.value,
        transmissionType: form.transmissionType.value,
        driveType: form.driveType.value,
        fuelType: form.fuelType.value,
        isGuaranteedModel: form.isGuaranteedModel.value,
        hasPrice: form.hasPrice.value,
        hasAirConditioning: form.hasAirConditioning.value,
        isOneWayRentalAllowed: form.isOneWayRentalAllowed.value,
        statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE,
        alternativeCars: this.convertAlternatives(),
        carModelFeatures: this.convertFeatures()
      };

      this.props.addNewCarModel(request);
    }
  };

  async onImageUpload(image: File) {
    const base64: any = await this.getBase64(image);
    this.form.patchValue({ image: base64.split(',')[ 1 ] });
  }

  // eslint-disable-next-line class-methods-use-this
  getBase64(image: File) {
    return new Promise(((resolve) => {
      const fileReader: FileReader = new FileReader();

      fileReader.onloadend = () => {
        resolve(fileReader.result);
      };
      fileReader.readAsDataURL(image);
    }));
  }

  convertAlternatives = () => {
    const alternativeCars: AlternativeCarModel[] = [];

    this.state.selectedAlternativeCars.forEach((a: any) => alternativeCars.push({ id: a }));

    return alternativeCars;
  };

  convertFeatures = () => {
    const carModelFeatures: CarModelFeatureModel[] = [];

    this.state.selectedFeatures.forEach((f: any) => carModelFeatures.push({ featureCode: f }));

    return carModelFeatures;
  };

  handleFeaturesSelectChange = (v: any) => {
    this.setState({ selectedFeatures: v.map((itm: any) => itm.code) });
  };

  handleALternativeCarSelectChange = (v: any) => {
    this.setState({ selectedAlternativeCars: v.map((itm: any) => itm.id) });
  };


  getFeatures = (features: CarModelFeatureModel[]) => {
    return (
      <ul className="details-list">
        {features.map((feature: CarModelFeatureModel) => {
          const fullFeature = this.state.carModelFeatures.find((cmf) => cmf.code === feature.featureCode);

          return (
            <li key={feature.featureCode}>
              {this.props.user && userCanAccessComponent(this.props.user, adminModuleEditRoles) ? (
                <Link to={`/admin/classifiers/${feature.featureCode}/view`} target="_blank" rel="noopener noreferrer">
                  {fullFeature?.name ?? feature.featureCode}
                </Link>
              ) : <div>{fullFeature?.name ?? feature.featureCode}</div>}
            </li>
          );
        })}
      </ul>
    );
  };

  getAlternativeCars = (alternativeCars: AlternativeCarModel[]) => {
    return (
      <ul className="details-list">
        {alternativeCars.map((alternativeCar: AlternativeCarModel) => {
          const fullCar = this.props.carModels.find((c) => c.id === alternativeCar.id);

          return (
            <li key={alternativeCar.id}>
              <Link to={`/setup/carmodels/${alternativeCar.id}/view`} target="_blank" rel="noopener noreferrer">
                {fullCar?.name ?? alternativeCar.id}
              </Link>
            </li>
          );
        })}
      </ul>
    );
  };

  updateFormValue = () => {
    if (!this.props.selectedCarModel) {
      return;
    }

    this.form.patchValue({
      acrissCode: this.props.selectedCarModel.acrissCode,
      passengerCount: this.props.selectedCarModel.passengerCount,
      doorCount: this.props.selectedCarModel.doorCount,
      baggageCountBig: this.props.selectedCarModel.baggageCountBig,
      baggageCountSmall: this.props.selectedCarModel.baggageCountSmall,
      youngDriverAge: this.props.selectedCarModel.youngDriverAge,
      minDriverAge: this.props.selectedCarModel.minDriverAge,
      name: this.props.selectedCarModel.name,
      tag: this.props.selectedCarModel.tag,
      image: this.props.selectedCarModel.image.original,
      supplierReferenceCode: this.props.selectedCarModel.supplierReferenceCode,
      displayName: this.props.selectedCarModel.displayName,
      transmissionType: this.props.selectedCarModel.transmissionType,
      driveType: this.props.selectedCarModel.driveType,
      fuelType: this.props.selectedCarModel.fuelType,
      isGuaranteedModel: this.props.selectedCarModel.isGuaranteedModel,
      hasPrice: this.props.selectedCarModel.hasPrice,
      isOneWayRentalAllowed: this.props.selectedCarModel.isOneWayRentalAllowed,
      hasAirConditioning: this.props.selectedCarModel.hasAirConditioning,
      active: this.props.selectedCarModel.statusType,
      alternatives: {
        ...this.form.controls.alternatives.value,
        selected: this.props.selectedCarModel ? this.props.selectedCarModel.alternativeCars.map((v) => v.id) : this.form.controls.alternatives.value.selected
      },
      features: {
        ...this.form.controls.features.value,
        selected: this.props.selectedCarModel ? this.props.selectedCarModel.carModelFeatures.map((v) => v.featureCode) : this.form.controls.features.value.selected
      }
    });
  };

  assignAlternativeCarsToForm = (alternativeCars: CarModelModel[]) => {
    const form = this.form.controls.alternatives.value as TwoColumnsSelectValueModel;
    alternativeCars.forEach((item) => {
      form.items.push(item);
    });

    if (this.props.selectedCarModel) {
      const ids = this.props.selectedCarModel.alternativeCars.map((itm: AlternativeCarModel) => itm.id);
      form.selected = ids;
      this.setState({
        selectedAlternativeCars: ids
      });
    }
  };

  onCancel = () => {
    if (this.props.isNew) {
      this.props.history.push('/setup/carmodels/');
    } else {
      this.updateFormValue();
      this.setState({ isEdit: false, canEditFeatures: false, canEditAlternativeCars: false });
    }
  };

  onEdit = () => {
    this.setState({ isEdit: true, canEditFeatures: true });
  };

  patchField = (option: SelectedOption, field: keyof AcrissCodeRelatedFields) => {
    const acrissCodeFields: any = {
      transmissionType: this.form.controls.transmissionType.value,
      fuelType: this.form.controls.fuelType.value,
      driveType: this.form.controls.driveType.value,
      hasAirCo: this.form.controls.hasAirConditioning.value
    };
    acrissCodeFields[ field ] = option.value as any;
    this.checkIfAcrissCodeValid({ ...(acrissCodeFields as AcrissCodeRelatedFields) });

    this.form.patchValue({ [ field ]: option.value });
  };

  checkIfAcrissCodeValid = (vals: AcrissCodeRelatedFields) => {
    const currentState = this.state.acrissCodeInvalid;
    if (this.form.controls.acrissCode.value && this.form.controls.acrissCode.value.length >= 3) {
      const transDriveChar: keyof TransmissionDriveMap = this.form.controls.acrissCode.value.charAt(2);
      const transDriveVal = transmissionDriveMap[ transDriveChar ];
      if (transDriveVal) {
        const transmission = transDriveVal.transmission;
        const drive = transDriveVal.drive;
        currentState.transInvalid = !!(vals.transmissionType.length && transmission !== vals.transmissionType);
        currentState.driveInvalid = !!(vals.driveType.length && drive !== vals.driveType);
      }
    } else {
      currentState.transInvalid = false;
      currentState.driveInvalid = false;
    }

    if (this.form.controls.acrissCode.value && this.form.controls.acrissCode.value.length >= 4) {
      const fuelAcChar: keyof FuelAcMap = this.form.controls.acrissCode.value.charAt(3);
      const fuelAcVal = fuelAcMap[ fuelAcChar ];
      if (fuelAcVal) {
        const fuel = fuelAcVal.fuel;
        const ac = fuelAcVal.airCo === AirCoVal.WITH_AC;
        currentState.fuelInvalid = !!(vals.fuelType.length && fuel !== vals.fuelType);
        currentState.acInvalid = ac !== vals.hasAirCo;
      }
    } else {
      currentState.fuelInvalid = false;
      currentState.acInvalid = false;
    }

    this.setState({ acrissCodeInvalid: { ...currentState } });
  };

  alternativeCarsSection = (carModel: CarModelModel) => (this.state.canEditAlternativeCars ? (
      <>
        <SectionTitle
          title={this.props.t('section_title.car_model.alternatives').toUpperCase()}
        />
        <div className="d-flex row m-1">
          <FieldControl
            name="alternatives"
            render={(v) => (
              <MultiSelect
                {...v}
                handleValueChange={(val: any) => this.handleALternativeCarSelectChange(val)}
                selectedAttributeName="id"
                sortAttributeName="name"
                primaryAtr="acrissCode"
                filterAttributes={[ 'acrissCode', 'name' ]}
                secondaryAtr="name"
              />
            )}
          />
        </div>
      </>
    ) : (this.props.carModels.length ? (
      <>
        <SectionTitle
          title={this.props.t('section_title.car_model.alternatives').toUpperCase()}
        />
        <div className="d-flex row info-container mb-2">
          <div className="col-4 d-flex align-items-center justify-content-end"></div>
          <div
            className="col-8 d-flex align-items-center justify-content-start">{this.getAlternativeCars(carModel.alternativeCars)}</div>
        </div>
      </>) : '')
  );

  featuresSection = (carModel?: CarModelModel) => (this.state.canEditFeatures ? (
    <>
      <SectionTitle
        title="FEATURES"
      />
      <div className="d-flex row m-1">
        <FieldControl
          name="features"
          render={(v) => (
            <MultiSelect
              {...v}
              handleValueChange={(val: any) => this.handleFeaturesSelectChange(val)}
              selectedAttributeName="code"
              sortAttributeName="name"
              filterAttributes={[ 'name' ]}
              secondaryAtr="name"
            />
          )}
        />
      </div>
    </>
  ) : (
    <>
      {carModel && this.state.carModelFeatures.length ? (
        <>
          <SectionTitle
            title="FEATURES"
          />
          <div className="d-flex row info-container">
            <div className="col-4 d-flex align-items-center justify-content-end"></div>
            <div
              className="col-8 d-flex align-items-center justify-content-start">{this.getFeatures(carModel.carModelFeatures)}</div>
          </div>
        </>
      ) : <></>}
    </>
  ));

  render() {
    if (!this.props.isNew && !this.props.selectedCarModel) {
      return null;
    }

    const { t } = this.props;

    const showForm = this.state.isEdit || this.props.isNew;
    const canEdit = !showForm;
    const carModel = this.props.selectedCarModel;

    return (
      <main>
        <Breadcrumbs
          items={
            [
              { path: '/setup', label: 'Setup' },
              { path: '/setup/carmodels', label: 'Car Models' },
              {
                path: null,
                label: this.props.isNew ? 'New' : this.props.selectedCarModel && this.props.selectedCarModel.name || ''
              }
            ]
          }
        />
        <div className="page-content">
          <div className="row justify-content-end">
            <div className="col-12 d-flex align-items-center justify-content-between">
              <h1 className="mb-0">{`${!this.props.isNew && carModel ? carModel.name : 'Add new car model'}`}</h1>
            </div>
          </div>
          {carModel && !showForm && this.props.user && (
            <>
              {this.props.user && userCanAccessComponent(this.props.user, carModelEditRoles) ? (
                <SectionTitle
                  title={t('section_title.car_model.model').toUpperCase()}
                  showButton
                  buttonName={t('b.edit_Generic edit button').toUpperCase()}
                  onEditClick={() => this.onEdit()}
                  showDeleteButton
                  deleteButtonName={t('b.delete_Generic delete button').toUpperCase()}
                  onDeleteClick={() => this.setState({ isModalOpen: true })}
                />
              ) : (
                <SectionTitle
                  title={t('section_title.car_model.model').toUpperCase()}
                />
              )}
              <div className="d-flex">
                <div className="col-6">
                  <div className="d-flex row info-container">
                    <label
                      className="col-8 d-flex align-items-center justify-content-end">{t('l.car_model.status')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start"><StatusLabel
                      status={carModel.statusType}/></div>
                  </div>
                  <div className="d-flex row info-container">
                    <label
                      className="col-8 d-flex align-items-center justify-content-end">{t('l.car_model.name')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">{carModel.name}</div>
                  </div>
                  {this.props.supplier?.hasBookingWeb &&
                      <div className="d-flex row info-container">
                          <label className="col-8 d-flex align-items-center justify-content-end"
                          >{t('l.car_model.display_name')}: </label>
                          <Tooltip
                              className="dark-tooltip"
                              modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                              placement="bottom"
                              target="display-name-tooltip"
                              autohide={false}
                          >
                                <pre className="d-flex flex-wrap dark-tooltip-text">
                                   {t('comment.car_model.display_name')}
                                </pre>
                          </Tooltip>
                          <div
                              className="col-4 d-flex aliPgn-items-center justify-content-start">
                            {carModel.displayName}
                              <img src={infoIcon} className="i-info-icon" id="display-name-tooltip"/></div>
                      </div>
                  }
                  <div className="d-flex row info-container">
                    <label className="col-8 d-flex align-items-center justify-content-end">
                      {t('l.car_model.acriss')}:</label>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">{carModel.acrissCode}</div>
                  </div>
                  <div className="d-flex row info-container">
                    <label className="col-8 d-flex align-items-center justify-content-end">
                      {t('l.car_model.supplier_ref')}
                      <img src={infoIcon} className="i-info-icon" id="supplier-ref-tooltip"/>: </label>
                    <Tooltip
                      className="dark-tooltip"
                      modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                      placement="bottom"
                      target="supplier-ref-tooltip"
                      autohide={false}
                    >
                          <pre className="d-flex flex-wrap dark-tooltip-text">
                            {t('comment.car_model.supplier_ref')}
                          </pre>
                    </Tooltip>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">
                      {carModel.supplierReferenceCode}</div>
                  </div>
                  <div className="d-flex row info-container">
                    <label className="col-8 d-flex justify-content-end">{t('l.car_model.purpose')}: </label>
                    <div
                      className="col-4 d-flex justify-content-start">{carModel.hasPrice ? 'MAIN' : 'ALTERNATIVE'}</div>
                  </div>
                  <div className="d-flex row info-container">
                    <label className="col-8 d-flex justify-content-end">{t('l.car_model.tag')}
                      <img src={infoIcon} className="i-info-icon" id="tag-tooltip"/>: </label>
                    <Tooltip
                      className="dark-tooltip"
                      modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                      placement="bottom"
                      target="tag-tooltip"
                      autohide={false}
                    >
                          <pre className="d-flex flex-wrap dark-tooltip-text">
                            {t('comment.car_model.tag')}
                          </pre>
                    </Tooltip>
                    <div className="col-4 d-flex justify-content-start">{carModel.tag}</div>
                  </div>
                  <div className="d-flex row info-container">
                    <label className="col-8 d-flex align-items-center justify-content-end">
                      {t('l.car_model.guaranteed_model')}
                      <img src={infoIcon} className="i-info-icon" id="guaranteed-model-tooltip"/>:</label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      <div className={carModel.isGuaranteedModel === true ? 'text-success' : ''}>
                        {carModel.isGuaranteedModel === true ? 'YES' : 'NO'}
                      </div>
                      <Tooltip
                        className="dark-tooltip"
                        modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                        placement="bottom"
                        target="guaranteed-model-tooltip"
                        autohide={false}
                      >
                            <pre className="d-flex flex-wrap dark-tooltip-text">
                              {t('comment.car_model.guaranteed_model')}
                            </pre>
                      </Tooltip>
                    </div>
                  </div>
                </div>
                <div className="col-2">
                  <div className="d-flex row info-container">
                    <div className="col-2"></div>
                    <div className="car-image">
                      <img alt="carImage"
                           src={this.form.controls.image.value ? this.form.controls.image.value : placeHolder}/>
                    </div>

                  </div>

                </div>
                <div className="col-2">
                  {this.props.selectedCarModel?.image.tiny && (
                    <div className="d-flex row info-container">
                      <a target="_blank" rel="noreferrer" href={this.props.selectedCarModel?.image.tiny}>Tiny</a>
                    </div>
                  )}
                  {this.props.selectedCarModel?.image.small && (
                    <div className="d-flex row info-container">
                      <a target="_blank" rel="noreferrer" href={this.props.selectedCarModel?.image.small}>Small</a>
                    </div>
                  )}
                  {this.props.selectedCarModel?.image.medium && (
                    <div className="d-flex row info-container">
                      <a target="_blank" rel="noreferrer"
                         href={this.props.selectedCarModel?.image.medium}>Medium</a>
                    </div>
                  )}
                  {this.props.selectedCarModel?.image.large && (
                    <div className="d-flex row info-container">
                      <a target="_blank" rel="noreferrer" href={this.props.selectedCarModel?.image.large}>Large</a>
                    </div>
                  )}
                </div>
              </div>
              <SectionTitle
                title={t('section_title.car_model.info').toUpperCase()}
              />
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.car_model.transmission')}:</label>
                <div
                  className="col-2 d-flex align-items-center justify-content-start">{carModel.transmissionType}</div>
                <label className="col-2 d-flex align-items-center justify-content-end">
                  {t('l.car_model.passangers')}: </label>
                <div
                  className="col-2 d-flex align-items-center justify-content-start">{carModel.passengerCount}</div>
              </div>
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.car_model.drive')}:</label>
                <div className="col-2 d-flex align-items-center justify-content-start">{carModel.driveType}</div>
                <label className="col-2 d-flex align-items-center justify-content-end">
                  {t('l.car_model.doors')}: </label>
                <div className="col-2 d-flex align-items-center justify-content-start">{carModel.doorCount}</div>
              </div>
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.car_model.fuel')}:</label>
                <div className="col-2 d-flex align-items-center justify-content-start">{carModel.fuelType}</div>
                <label className="col-2 d-flex align-items-center justify-content-end">
                  {t('l.car_model.baggage_large')}
                  <img src={infoIcon} className="i-info-icon" id="baggage-count-large-tooltip"/>:</label>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="baggage-count-large-tooltip"
                  autohide={false}
                >
                      <pre className="dark-tooltip-text">
                        {t('comment.car_model.baggage_large')}
                      </pre>
                </Tooltip>
                <div
                  className="col-2 d-flex align-items-center justify-content-start">{carModel.baggageCountBig}
                </div>
              </div>
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.car_model.air_conditioning')}:</label>
                <div className="col-2 d-flex align-items-center justify-content-start">
                  <div className={carModel.hasAirConditioning === true ? 'text-success' : ''}>
                    {carModel.hasAirConditioning === true ? 'YES' : 'NO'}
                  </div>
                </div>
                <label className="col-2 d-flex align-items-center justify-content-end">
                  {t('l.car_model.baggage.small')}
                  <img src={infoIcon} className="i-info-icon" id="baggage-count-small-tooltip"/>: </label>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="baggage-count-small-tooltip"
                  autohide={false}
                >
                      <pre className="dark-tooltip-text">
                        {t('comment.car_model.baggage_small')}
                      </pre>
                </Tooltip>
                <div
                  className="col-2 d-flex align-items-center justify-content-start">{carModel.baggageCountSmall}</div>
              </div>
              <SectionTitle
                title={t('section_title.car_model.pricing').toUpperCase()}
              />
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.car_model.one_way_retal')}</label>
                <div className="col-2 d-flex align-items-center justify-content-start">
                  <div className={carModel.isOneWayRentalAllowed === true ? 'text-success' : ''}>
                    {carModel.isOneWayRentalAllowed === true ? 'YES' : 'NO'}
                  </div>
                </div>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="min-driver-age-tooltip"
                  autohide={false}
                >
                      <pre className="dark-tooltip-text">
                        {t('comment.car_model.min_driver_age')}
                      </pre>
                </Tooltip>
                <label className="col-2 d-flex align-items-center justify-content-end">
                  {t('l.car_model.min_driver_age')}
                  <img src={infoIcon} className="i-info-icon" id="min-driver-age-tooltip"/>:</label>
                <div className="col-2 d-flex align-items-center justify-content-start">{carModel.minDriverAge}</div>
              </div>
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">Has price in:</label>
                <div className="col-2 d-flex align-items-center justify-content-start">
                  {carModel.associatedRatePlansNo ? carModel.associatedRatePlansNo : 0} rate plans
                </div>
                <label className="col-2 d-flex align-items-center justify-content-end">
                  {t('l.car_model.young_driver_age')}
                  <img src={infoIcon} className="i-info-icon" id="young-driver-age-tooltip"/>: </label>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="young-driver-age-tooltip"
                  autohide={false}
                >
                      <pre className="dark-tooltip-text">
                        {t('comment.car_model.young_driver_age')}
                      </pre>
                </Tooltip>
                <div
                  className="col-2 d-flex align-items-center justify-content-start">{carModel.youngDriverAge}</div>
              </div>
            </>
          )}
          <FieldGroup
            strict={false}
            control={this.form}
            render={() => (
              <form className="form-horizontal" onSubmit={(e) => this.onSubmit(e)}>
                {!showForm && carModel && (
                  <>
                    {this.props.supplier?.hasBookingWeb && this.featuresSection(carModel)}
                    {this.alternativeCarsSection(carModel)}
                  </>
                )}
                {showForm && (
                  <>
                    <div className="mb-2"/>
                    <SectionTitle
                      title={t('section_title.car_model.model').toUpperCase()}
                    />
                    <div className="form-group row">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.car_model.status')}:</label>
                      </div>
                      <div className="col-6 d-flex align-items-center justify-content-start">
                        <FieldControl
                          name="active"
                          render={(props) => <Checkbox {...props} id="statusField" className="mt-1"
                                                       disabled={canEdit}>Active</Checkbox>}
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.car_model.name')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="name"
                            render={TextInput}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.car_model.acriss')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            strict={false}
                            name="acrissCode"
                            render={(props) => <TextInput {...props} type="text"
                                                          disabled={!this.props.isNew}></TextInput>}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>Has price:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start">
                          <FieldControl
                            strict={false}
                            name="hasPrice"
                            render={(props) => <Checkbox {...props} id="hasPrice" className="mt-1"
                                                         disabled={canEdit}></Checkbox>}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.car_model.supplier_ref')}
                            <img src={infoIcon} className="i-info-icon" id="supplier-input-tooltip"/>:</label>
                        </div>
                        <Tooltip
                          className="dark-tooltip"
                          modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                          placement="bottom"
                          target="supplier-input-tooltip"
                          autohide={false}
                        >
                          <div className="d-flex flex-wrap dark-tooltip-text">
                            {t('comment.car_model.supplier_ref')}
                          </div>
                        </Tooltip>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="supplierReferenceCode"
                            render={TextInput}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.car_model.display_name')}
                            <img src={infoIcon} className="i-info-icon" id="display-name-input-tooltip"/>:</label>
                        </div>
                        <Tooltip
                          className="dark-tooltip"
                          modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                          placement="bottom"
                          target="display-name-input-tooltip"
                          autohide={false}
                        >
                            <pre className="dark-tooltip-text">
                              {t('comment.car_model.display_name')}
                            </pre>
                        </Tooltip>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="displayName"
                            render={TextInput}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.car_model.tag')}
                            <img src={infoIcon} className="i-info-icon" id="tag-input-tooltip"/>:</label>
                        </div>
                        <Tooltip
                          className="dark-tooltip"
                          modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                          placement="bottom"
                          target="tag-input-tooltip"
                          autohide={false}
                        >
                            <pre className="dark-tooltip-text">
                              {t('comment.car_model.tag')}
                            </pre>
                        </Tooltip>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="tag"
                            render={TextInput}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.car_model.guaranteed_model')}
                            <img src={infoIcon} className="i-info-icon" id="guaranteed-model-input-tooltip"/>:</label>
                        </div>
                        <Tooltip
                          className="dark-tooltip"
                          modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                          placement="bottom"
                          target="guaranteed-model-input-tooltip"
                          autohide={false}
                        >
                            <pre className="dark-tooltip-text">
                              {t('comment.car_model.guaranteed_model')}
                            </pre>
                        </Tooltip>
                        <div className="col-6 d-flex align-items-center justify-content-start">
                          <FieldControl
                            strict={false}
                            name="isGuaranteedModel"
                            render={(props) => <Checkbox {...props} id="isGuaranteedModel" className="mt-1"
                                                         disabled={canEdit}></Checkbox>}
                          />
                        </div>
                      </div>
                      <SectionTitle
                        title={t('section_title.car_model.info').toUpperCase()}
                      />
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.car_model.transmission')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            strict={false}
                            name="transmissionType"
                            render={(props) => (
                              <Select
                                value={carModelTransmissionTypeOptions.find((select) => select.value === this.form.controls.transmissionType.value)}
                                className="mr-1"
                                placeholder="transmission type"
                                disableMinWidth
                                options={(carModelTransmissionTypeOptions)}
                                closeMenuOnSelect={false}
                                disableClear
                                hideSelectedOptions={false}
                                label="transmission types"
                                onChange={(value) => this.patchField(value as SelectedOption, 'transmissionType')}
                              />
                            )}
                          />
                        </div>
                        {
                          this.state.acrissCodeInvalid.transInvalid &&
                            <>
                                <div className="form-input-danger" id="danger-transmission">
                                </div>
                                <Tooltip
                                    modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                                    placement="bottom"
                                    target="danger-transmission"
                                    autohide={false}
                                >
                                    <div className="d-flex flex-wrap">
                                        Selected transmission type doesn&apos;t correspond to acriss code
                                    </div>
                                </Tooltip>
                            </>
                        }
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.car_model.drive')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            strict={false}
                            name="driveType"
                            render={(props) => (
                              <Select
                                value={carModelDriveTypeOptions.find((select) => select.value === this.form.controls.driveType.value)}
                                className="mr-1"
                                disableClear
                                disableMinWidth
                                placeholder="drive type"
                                options={(carModelDriveTypeOptions)}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="drive types"
                                onChange={(value) => this.patchField(value as SelectedOption, 'driveType')}
                              />
                            )}
                          />
                        </div>
                        {
                          this.state.acrissCodeInvalid.driveInvalid &&
                            <>
                                <div className="form-input-danger" id="danger-drive">
                                </div>
                                <Tooltip
                                    modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                                    placement="bottom"
                                    target="danger-drive"
                                    autohide={false}
                                >
                                    <div className="d-flex flex-wrap">
                                        Selected drive type doesn&apos;t correspond to acriss code
                                    </div>
                                </Tooltip>
                            </>
                        }
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.car_model.fuel')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            strict={false}
                            name="fuelType"
                            render={(props) => (
                              <Select
                                value={carModelFuelTypeOptions.find((select) => select.value === this.form.controls.fuelType.value)}
                                className="mr-1"
                                disableClear
                                disableMinWidth
                                placeholder="fuel type"
                                options={(carModelFuelTypeOptions)}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="fuel types"
                                onChange={(value) => this.patchField(value as SelectedOption, 'fuelType')}
                              />
                            )}
                          />
                        </div>
                        {
                          this.state.acrissCodeInvalid.fuelInvalid &&
                            <>
                                <div className="form-input-danger" id="danger-fuel">
                                </div>
                                <Tooltip
                                    modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                                    placement="bottom"
                                    target="danger-fuel"
                                    autohide={false}
                                >
                                    <div className="d-flex flex-wrap">
                                        Selected fuel type doesn&apos;t correspond to acriss code
                                    </div>
                                </Tooltip>
                            </>
                        }
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.car_model.air_conditioning')}:</label>
                        </div>
                        <div className="col-6 d-flex align-items-center justify-content-start">
                          <FieldControl
                            strict={false}
                            name="hasAirConditioning"
                            render={(props) => <Checkbox {...props} id="hasAirConditioning" className="mt-1"
                                                         disabled={canEdit}></Checkbox>}
                          />
                          {
                            this.state.acrissCodeInvalid.acInvalid &&
                              <>
                                  <div className="form-input-danger" id="danger-ac">
                                  </div>
                                  <Tooltip
                                      modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                                      placement="bottom"
                                      target="danger-ac"
                                      autohide={false}
                                  >
                                      <div className="d-flex flex-wrap">
                                          Selected air conditioning option doesn&apos;t correspond to acriss code
                                      </div>
                                  </Tooltip>
                              </>
                          }
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.car_model.passangers')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="passengerCount"
                            render={NumberInput}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.car_model.doors')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="doorCount"
                            render={NumberInput}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.car_model.baggage_large')}
                            <img src={infoIcon} className="i-info-icon" id="baggage-large-input-tooltip"/>:</label>
                        </div>
                        <Tooltip
                          className="dark-tooltip"
                          modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                          placement="bottom"
                          target="baggage-large-input-tooltip"
                          autohide={false}
                        >
                            <pre className="dark-tooltip-text">
                              {t('comment.car_model.baggage_large')}
                            </pre>
                        </Tooltip>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="baggageCountBig"
                            render={NumberInput}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.car_model.baggage.small')}
                            <img src={infoIcon} className="i-info-icon" id="baggage-small-input-tooltip"/>:</label>
                        </div>
                        <Tooltip
                          className="dark-tooltip"
                          modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                          placement="bottom"
                          target="baggage-small-input-tooltip"
                          autohide={false}
                        >
                            <pre className="dark-tooltip-text">
                              {t('comment.car_model.baggage_small')}
                            </pre>
                        </Tooltip>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="baggageCountSmall"
                            render={NumberInput}
                          />
                        </div>
                      </div>
                      <SectionTitle
                        title={t('section_title.car_model.pricing').toUpperCase()}
                      />
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.car_model.one_way_retal')}:</label>
                        </div>
                        <div className="col-6 d-flex align-items-center justify-content-start">
                          <FieldControl
                            strict={false}
                            name="isOneWayRentalAllowed"
                            render={(props) => <Checkbox {...props} id="isOneWayRentalAllowed" className="mt-1"
                                                         disabled={canEdit}></Checkbox>}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.car_model.young_driver_age')}
                            <img src={infoIcon} className="i-info-icon" id="young-driver-age-input-tooltip"/>:</label>
                        </div>
                        <Tooltip
                          className="dark-tooltip"
                          modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                          placement="bottom"
                          target="young-driver-age-input-tooltip"
                          autohide={false}
                        >
                            <pre className="dark-tooltip-text">
                              {t('comment.car_model.young_driver_age')}
                            </pre>
                        </Tooltip>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="youngDriverAge"
                            render={NumberInput}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.car_model.min_driver_age')}
                            <img src={infoIcon} className="i-info-icon" id="min-driver-age-input-tooltip"/>:</label>
                        </div>
                        <Tooltip
                          className="dark-tooltip"
                          modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                          placement="bottom"
                          target="min-driver-age-input-tooltip"
                          autohide={false}
                        >
                            <pre className="dark-tooltip-text">
                              {t('comment.car_model.min_driver_age')}
                            </pre>
                        </Tooltip>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="minDriverAge"
                            render={NumberInput}
                          />
                        </div>
                      </div>
                      <SectionTitle
                        title={t('section_title.car_model.image').toUpperCase()}
                      />
                      <div className="form-group row">
                        <div className="col-6 d-flex align-items-center justify-content-center">
                          <ImageUpload
                            image={this.form.controls.image.value}
                            isEdit
                            onImageSelect={(file: File) => this.onImageUpload(file)}
                          />
                        </div>
                      </div>
                    </div>
                    <>
                      {this.props.supplier?.hasBookingWeb && this.featuresSection()}
                    </>
                  </>
                )}
                {showForm && (
                  <>
                    <hr/>
                    <div className="row mt-2">
                      <div className="col-12 text-right">
                        <button type="button" className="btn btn-outline-secondary btn-lg mr-2" onClick={this.onCancel}>
                          {t('b.cancel_Generic cancel button translation').toUpperCase()}</button>
                        <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
                          {t('b.save_Generic save button translation').toUpperCase()}</button>
                      </div>
                    </div>
                  </>

                )}
              </form>
            )}
          />
          {carModel && !showForm && this.props.user ? (
            <>
              <SectionTitle title={t('section_title.audit').toUpperCase()}/>
              <div className="d-flex">
                <div className="col-8">
                  <div className="d-flex row info-container">
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.created_by')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {carModel.createdBy ? carModel.createdBy : 'System'}{carModel.createdAt ? ` @ ${formatDateTime(carModel.createdAt)}` : ''}
                    </div>
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.modified_by')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {carModel.modifiedBy ? carModel.modifiedBy : 'System'}{carModel.modifiedAt ? ` @ ${formatDateTime(carModel.modifiedAt)}` : ''}
                    </div>
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.log')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      <Link to={`/setup/carmodels/${carModel.id}/audit`}
                            className="btn btn-text-only"
                            target="_blank"
                            rel="noopener noreferrer">
                        {t('b.car_model.view_audit_log')}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </>


          ) : ''}
        </div>
        <ConfirmationModal
          title="Confirm car model removal"
          content="Are you sure you want to remove this car model?"
          isOpen={this.state.isModalOpen}
          toggle={() => this.setState({ isModalOpen: false })}
          onConfirm={() => this.onDeleteConfirm()}
          onCancel={() => this.setState({ isModalOpen: false })}
        />
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  selectedCarModel: state.setup.carModels.selectedCarModel,
  carModels: state.setup.carModels.carModels,
  filters: state.setup.carModels.filters,
  postSuccess: state.setup.carModels.postSuccess,
  lastFetch: state.setup.carModels.lastFetch,
  user: state.authentication.user,
  supplier: state.authentication.activeSupplier
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, CarModelActionTypes>) => ({
  getSelectedCarModel: (id: number) => dispatch(getCarModel(id)),
  addNewCarModel: (request: CarModelRequest) => dispatch(addNewCarModel(request)),
  editCarModel: (id: number, request: CarModelRequest) => dispatch(editCarModel(id, request)),
  deleteCarModel: (id: number) => dispatch(deleteCarModel(id)),
  getCarModels: async () => dispatch(getCarModelsList())
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(CarModelDetails));
